import React, { useState, useContext } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingCart,
  Plus,
  Minus,
  X,
  Truck,
  Shield,
  CreditCard,
  Tag,
  ChevronRight,
  ArrowLeft,
  Trash2,
  Gift,
} from "lucide-react";
import { CartContext } from "./cartContext";
import { CurrencyContext } from "./CurrencyContext";

export default function Cart() {
  const { cartItems, removeFromCart, updateQuantity, clearCart } =
    useContext(CartContext);
    const {currencySign, convertPrice} = useContext(CurrencyContext);

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [selectedShipping, setSelectedShipping] = useState("standard");
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const shippingOptions = [
    { id: "standard", name: "Standard", price: 9.99, days: "5-7 days" },
    { id: "express", name: "Express", price: 19.99, days: "2-3 days" },
    { id: "overnight", name: "Overnight", price: 39.99, days: "Next day" },
  ];

  const coupons = {
    SAVE10: { discount: 0.1, type: "percentage", description: "10% off" },
    SAVE20: { discount: 20, type: "fixed", description: "$20 off" },
    FREESHIP: { discount: 0, type: "shipping", description: "Free Shipping" },
  };

  const applyCoupon = () => {
    if (coupons[couponCode.toUpperCase()]) {
      setAppliedCoupon(coupons[couponCode.toUpperCase()]);
      setCouponCode("");
    }
  };

  const removeCoupon = () => setAppliedCoupon(null);

  const calculateSubtotal = () =>
    cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    const subtotal = calculateSubtotal();
    if (appliedCoupon.type === "percentage") return subtotal * appliedCoupon.discount;
    if (appliedCoupon.type === "fixed") return appliedCoupon.discount;
    return 0;
  };

  const calculateShipping = () => {
    if (appliedCoupon?.type === "shipping") return 0;
    const shipping = shippingOptions.find((opt) => opt.id === selectedShipping);
    return shipping?.price || 0;
  };

  const calculateTax = () => (calculateSubtotal() - calculateDiscount()) * 0.08;

  const calculateTotal = () =>
    calculateSubtotal() - calculateDiscount() + calculateShipping() + calculateTax();

  const handleCheckout = () => {
    setIsCheckingOut(true);
    setTimeout(() => {
      
      setIsCheckingOut(false);
    }, 2000);
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mb-4 mx-auto">
            <ShoppingCart className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
          <p className="text-gray-600 mb-4">Add products to start shopping!</p>
          <Link to={"/"} className="bg-[#fe7590] text-white hover:bg-red-700 px-4 py-2 rounded-xl shadow">Shop Now</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-sm divide-y">
            {cartItems.map((item) => (
              <div key={item.id} className="flex gap-4 p-6">
                <img src={item.image} alt={item.name} className="w-24 h-24 object-cover rounded" />
                <div className="flex-1 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{item.name}</h3>
                      {item.freeShipping && (
                        <span className="text-green-600 flex items-center text-xs mt-1">
                          <Truck className="w-3 h-3 mr-1" /> Free Shipping
                        </span>
                      )}
                    </div>
                    <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-[#fe7590]">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 border rounded-full flex items-center justify-center hover:bg-gray-100"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 border rounded-full flex items-center justify-center hover:bg-gray-100"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{currencySign}{convertPrice((item.price * item.quantity).toFixed(2))}</div>
                      {item.originalPrice > item.price && (
                        <div className="text-sm text-gray-500 line-through">
                          {currencySign}{convertPrice((item.originalPrice * item.quantity).toFixed(2))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Coupon */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold mb-3">Coupon Code</h3>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                className="flex-1 border px-3 py-2 rounded focus:outline-none focus:border-blue-500"
              />
              <button onClick={applyCoupon} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                Apply
              </button>
            </div>
            {appliedCoupon && (
              <div className="mt-3 flex justify-between items-center bg-green-50 p-2 rounded border border-green-200">
                <span className="text-green-700 text-sm">{appliedCoupon.description} applied</span>
                <button onClick={removeCoupon} className="text-green-600 hover:text-green-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24 space-y-4">
          <h2 className="font-semibold text-lg mb-2">Order Summary</h2>
          <div className="flex justify-between text-sm">
            <span>Subtotal</span>
            <span>{currencySign}{convertPrice(calculateSubtotal().toFixed(2))}</span>
          </div>
          {appliedCoupon && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount</span>
              <span>{currencySign}{convertPrice(calculateDiscount().toFixed(2))}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span>Shipping</span>
            <span>{currencySign}{convertPrice(calculateShipping().toFixed(2))}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Tax (8%)</span>

            <span>{currencySign}{convertPrice(calculateTax().toFixed(2))}</span>
          </div>
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span>Total</span>

            <span>{currencySign}{convertPrice(calculateTotal().toFixed(2))}</span>
          </div>

          <Link to={"/checkout"}>
          <button
            onClick={handleCheckout}
            disabled={isCheckingOut}
            className="w-full bg-blue-600 text-white py-3 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
            Proceed to Checkout
          </button>
            </Link>
        </div>
      </div>
    </div>
  );
}
